// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import type {
  OnboardingStateResponse,
  OnboardingStepAnswer,
  PantryCandidate,
  ProfileResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { ApiError } from "@/api/client"
import { trackFunnelEvent } from "@/lib/analytics"
import { useHealthConsentRequired } from "@/lib/healthConsentGate"
import {
  DEFERRED_STEPS,
  STEP_SCHEMAS,
  TOTAL_STEPS,
  VISUAL_STEP_ORDER,
  deferredDefaultAnswer,
  isValidStepAnswer,
  visualPosition,
  type WireStep,
} from "./steps"
import { useI18n } from "@/i18n"
import type { GoalType } from "./calorieTarget"
import type { AnswerDraft, Blocker, CalorieTargetDraft, DraftAnswers, FinishPhase } from "./types"

export type LoadState = "loading" | "ready" | "error"

/** Matches PlanPage.tsx's generatePlan() polling idiom: 15 attempts, 2s apart. */
const PLAN_POLL_ATTEMPTS = 15
const PLAN_POLL_INTERVAL_MS = 2000

async function pollPlanJobStatus(
  api: ReturnType<typeof useApiClient>,
  planJobId: string
): Promise<"completed" | "failed" | "timedOut"> {
  for (let attempt = 0; attempt < PLAN_POLL_ATTEMPTS; attempt++) {
    await new Promise(resolve => setTimeout(resolve, PLAN_POLL_INTERVAL_MS))
    try {
      const job = await api.getPlanJob(planJobId)
      if (job.status === "completed" || job.status === "failed") return job.status
    } catch {
      // transient poll failure — keep polling until the attempt budget runs out
    }
  }
  return "timedOut"
}

export interface PantryExtractionState {
  sourceText: string
  candidates: PantryCandidate[]
  confirmationToken: string
}

/**
 * Owns the entire T11 onboarding flow's state: load/resume, per-step
 * drafts, save-on-continue, the calorie-target/pregnancy-goal correction
 * blockers, pantry quick-add extraction+confirmation, and the final
 * review -> plan-job trigger. Deliberately framework-light (no router
 * state for the step position) so a mid-flow language switch — which only
 * re-renders the same component tree with a different `useI18n()` locale —
 * never touches this hook's state at all.
 */
export function useOnboardingFlow() {
  const api = useApiClient()
  const navigate = useNavigate()
  const { locale } = useI18n()

  const [loadState, setLoadState] = useState<LoadState>("loading")
  const [loadError, setLoadError] = useState<string | null>(null)
  const [visualIndex, setVisualIndex] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<Set<WireStep>>(new Set())
  const [draftAnswers, setDraftAnswers] = useState<DraftAnswers>({})
  const [calorieTarget, setCalorieTarget] = useState<CalorieTargetDraft | null>(null)
  // True only when the USER adjusted/acknowledged the target in this session.
  // The draft is also seeded from GET /v1/profile on load (resume case); a
  // seeded value must never be PATCHed back — the server recomputes targets
  // during steps 3/4/13, so writing the seed back would overwrite a fresher
  // calculated target with a stale copy and bump the profile revision (which
  // marks the just-generated plan "may not match your latest settings").
  const [calorieTargetDirty, setCalorieTargetDirty] = useState(false)
  /** Error code · correlationId of a finish failure — shown on the error screen so live reports are diagnosable (2026-08-29 incident). */
  const [finishErrorDetail, setFinishErrorDetail] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [blocker, setBlocker] = useState<Blocker>(null)
  const [pantryExtraction, setPantryExtraction] = useState<PantryExtractionState | null>(null)
  const [finishPhase, setFinishPhase] = useState<FinishPhase>("idle")
  const [pendingPlanJobId, setPendingPlanJobId] = useState<string | null>(null)
  // Explicit health/AI consent (Art. 9(2)(a)): null until /v1/me answered; a failed lookup reads as
  // "not given" (fail closed — the server refuses health steps without it anyway).
  const [healthConsentGiven, setHealthConsentGiven] = useState<boolean | null>(null)
  // A 403 HEALTH_AI_CONSENT_REQUIRED from any call in this flow (consent withdrawn mid-session) lands here too.
  const consentRefused = useHealthConsentRequired()
  useEffect(() => {
    if (consentRefused) setHealthConsentGiven(false)
  }, [consentRefused])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoadState("loading")
      setLoadError(null)
      try {
        const [onboarding, profile, consented] = await Promise.all([
          api.getOnboarding(),
          safeGetProfile(api),
          safeGetHealthConsent(api),
        ])
        if (cancelled) return
        applyServerState(onboarding, profile)
        setHealthConsentGiven(consented)
        setLoadState("ready")
      } catch (err) {
        if (cancelled) return
        setLoadError(err instanceof ApiError ? err.envelope.messageKey : "errors.unknown")
        setLoadState("error")
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  function applyServerState(onboarding: OnboardingStateResponse, profile: ProfileResponse | null) {
    const nextDrafts: DraftAnswers = {}
    const nextCompleted = new Set<WireStep>()
    for (const a of onboarding.answers) {
      const step = a.step as WireStep
      nextDrafts[step] = a.answer as AnswerDraft
    }
    for (const s of onboarding.completedSteps) {
      nextCompleted.add(s as WireStep)
    }
    setDraftAnswers(nextDrafts)
    setCompletedSteps(nextCompleted)
    if (profile) {
      setCalorieTarget({
        kcal: profile.calorieTarget.kcal,
        source: profile.calorieTarget.source,
        floorAcknowledged: profile.calorieTarget.floorAcknowledged,
      })
    }
    // Resume at the first step that isn't complete yet; if every step is
    // complete, land on the final review step so the user can still edit.
    const firstIncomplete = VISUAL_STEP_ORDER.find(s => !nextCompleted.has(s))
    const resumeStep = firstIncomplete ?? VISUAL_STEP_ORDER[VISUAL_STEP_ORDER.length - 1]
    setVisualIndex(visualPosition(resumeStep) - 1)
  }

  const currentStep = VISUAL_STEP_ORDER[visualIndex]
  const isFirstStep = visualIndex === 0
  const isLastStep = visualIndex === VISUAL_STEP_ORDER.length - 1

  const currentAnswer = draftAnswers[currentStep] ?? {}

  // The consent screen is an interstitial BEFORE the first health step. Onboarding persists per step, and the
  // first visual step (wire 2: 18+ confirmation, name, date of birth) carries no health data; every later one
  // does (goal, body, diet, allergies, conditions) — so past the first step, no consent means no next screen.
  // Resume lands here too when step 2 is done and the consent is missing. Not counted in the step progress.
  const needsHealthConsent = healthConsentGiven === false && visualIndex > 0
  const recordHealthConsent = useCallback(() => setHealthConsentGiven(true), [])

  const setCurrentAnswer = useCallback(
    (patch: AnswerDraft | ((prev: AnswerDraft) => AnswerDraft)) => {
      setDraftAnswers(prev => {
        const prevAnswer = prev[currentStep] ?? {}
        const nextAnswer =
          typeof patch === "function" ? patch(prevAnswer) : { ...prevAnswer, ...patch }
        return { ...prev, [currentStep]: nextAnswer }
      })
    },
    [currentStep]
  )

  const currentStepValid = useMemo(
    () => isValidStepAnswer(currentStep, draftAnswers[currentStep] ?? {}),
    [currentStep, draftAnswers]
  )

  const goBack = useCallback(() => {
    setSaveError(null)
    setBlocker(null)
    setVisualIndex(i => Math.max(0, i - 1))
  }, [])

  const jumpToStep = useCallback((step: WireStep) => {
    setSaveError(null)
    setBlocker(null)
    setVisualIndex(visualPosition(step) - 1)
  }, [])

  const goNext = useCallback(async () => {
    const answer = draftAnswers[currentStep] ?? {}
    if (!STEP_SCHEMAS[currentStep].safeParse({ step: currentStep, answer }).success) return

    setSaving(true)
    setSaveError(null)
    try {
      await api.putOnboardingStep(currentStep, {
        step: currentStep,
        answer,
      } as unknown as OnboardingStepAnswer)
      setCompletedSteps(prev => new Set(prev).add(currentStep))
      if (!isLastStep) {
        setVisualIndex(i => i + 1)
      }
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.envelope.messageKey : "errors.unknown")
    } finally {
      setSaving(false)
    }
  }, [api, currentStep, draftAnswers, isLastStep])

  // --- Ineligibility (client-only; never sent to the server) -------------
  const setIneligible = useCallback((kind: "ineligible_age" | "ineligible_ed_history") => {
    setBlocker({ kind })
  }, [])

  // --- Calorie target / correction flow -----------------------------------
  const proposeCalorieTarget = useCallback((draft: CalorieTargetDraft, floorKcal: number) => {
    if (draft.kcal < floorKcal) {
      setBlocker({ kind: "below_floor_target", targetKcal: draft.kcal, floorKcal })
      return false
    }
    setBlocker(null)
    setCalorieTarget(draft)
    // Live incident 2026-08-30 round 2 (owner: "I didn't adjust any
    // target yet hit PROFILE_INCOMPLETE"): this runs for EVERY user — the
    // below-floor safety check evaluates the CALCULATED draft too — so
    // "dirty" here never meant "user edited". A plain calculated target
    // needs no PATCH at all (the step-4 PUT already makes the server
    // compute the identical value); only a manual target or an
    // acknowledged floor carries information the server doesn't have.
    setCalorieTargetDirty(draft.source === "manual" || draft.floorAcknowledged)
    return true
  }, [])

  const acceptFloorTarget = useCallback((floorKcal: number, source: "calculated" | "manual") => {
    setCalorieTarget({ kcal: floorKcal, source, floorAcknowledged: true })
    setCalorieTargetDirty(true)
    setBlocker(null)
  }, [])

  // --- Unsafe-goal blocking (pregnancy hard-blocked; breastfeeding gated) -
  // ADR-030 (Option B, 2026-08-27): pregnancy + lose_weight stays a hard
  // block (never overridable in onboarding). breastfeeding + lose_weight is
  // no longer blocked — it raises a non-blocking acknowledgement gate the
  // user must actively confirm (acknowledgeBreastfeedingLoss) before the
  // step submits; the server-side energy model (targets.ts, ADR-030) caps
  // the deficit and raises the floor regardless of whether this gate ran.
  const checkPregnancyGoalConflict = useCallback(
    (conditions: string[]) => {
      const goalAnswer = draftAnswers[3] as { goalType?: GoalType } | undefined
      const goalType = goalAnswer?.goalType
      if (goalType !== "loss") return false
      if (conditions.includes("pregnancy")) {
        setBlocker({ kind: "unsafe_goal_pregnancy", goalType })
        return true
      }
      if (conditions.includes("breastfeeding")) {
        setBlocker({ kind: "breastfeeding_loss_ack", goalType })
        return true
      }
      return false
    },
    [draftAnswers]
  )

  const clearBlocker = useCallback(() => setBlocker(null), [])

  // --- Pantry quick-add (step 12) -----------------------------------------
  const extractPantryText = useCallback(
    async (sourceText: string, locale: "de" | "en") => {
      const res = await api.extractPantryItems({ sourceText, locale })
      setPantryExtraction({
        sourceText,
        candidates: res.candidates,
        confirmationToken: res.confirmationToken,
      })
      return res
    },
    [api]
  )

  const confirmPantryCandidates = useCallback(
    async (candidates: Parameters<typeof api.confirmPantryItems>[0]["candidates"]) => {
      if (!pantryExtraction) throw new Error("no pending pantry extraction")
      await api.confirmPantryItems({
        confirmationToken: pantryExtraction.confirmationToken,
        candidates,
      })
    },
    [api, pantryExtraction]
  )

  const clearPantryExtraction = useCallback(() => setPantryExtraction(null), [])

  // --- Final submit -----------------------------------------------------
  // Item 35 fix: the old version fired a fire-and-forget createPlanJob and
  // navigated immediately, so the user saw the Continue button's label
  // change to "Saving…" and then the ENTIRE page was replaced by an
  // unrelated billing page (which itself flashed its own loading spinner) —
  // "the window vanished with an unreadable flash." This version takes over
  // the whole page with an honest full-screen state (see FinishingScreen)
  // and polls the plan job to completion/failure/timeout before handing off
  // to Access, so the user is never staring at nothing.
  const finishOnboarding = useCallback(async () => {
    setFinishPhase("submitting")
    setSaveError(null)
    setFinishErrorDetail(null)
    // Live incident 2026-08-29: one transient 4xx mid-finish surfaced the
    // full "We couldn't start your plan" screen although the immediate
    // retry succeeded. Every call in this sequence is idempotent, so ONE
    // silent retry after a short pause absorbs blips; a second failure is
    // real and surfaces WITH a diagnosable detail line.
    for (let attempt = 0; attempt < 2; attempt++) {
    try {
      // ADR-034: the LAST SHOWN step (meal structure, wire 10) is saved
      // here — pressing "Create my first plan" both saves it and finishes.
      const lastShown = VISUAL_STEP_ORDER[VISUAL_STEP_ORDER.length - 1]
      const lastAnswer = draftAnswers[lastShown] ?? {}
      if (STEP_SCHEMAS[lastShown].safeParse({ step: lastShown, answer: lastAnswer }).success) {
        await api.putOnboardingStep(lastShown, {
          step: lastShown,
          answer: lastAnswer,
        } as unknown as OnboardingStepAnswer)
        setCompletedSteps(prev => new Set(prev).add(lastShown))
      }

      // ADR-034 deferred steps: honest "nothing declared" defaults for the
      // screens the flow no longer shows — submitted ONLY when the server
      // has no answer yet (a resume user who filled the old long flow keeps
      // every answer). 12 goes last: its PUT completes onboarding and
      // triggers the first plan.
      for (const step of DEFERRED_STEPS) {
        if (step === 12) continue
        if (completedSteps.has(step) || draftAnswers[step] !== undefined) continue
        await api.putOnboardingStep(step, {
          step,
          answer: deferredDefaultAnswer(step, locale),
        } as unknown as OnboardingStepAnswer)
        setCompletedSteps(prev => new Set(prev).add(step))
      }

      // Ordering invariant (live incident 2026-08-30, PROFILE_INCOMPLETE):
      // a user-adjusted target PATCH must land AFTER the deferred-step PUTs
      // (step 8 fills cooking skill/enjoyment — before it, PATCH /v1/profile
      // deterministically 404s PROFILE_INCOMPLETE for every target-adjusting
      // new user) and BEFORE the completing PUT 12 (the first plan must be
      // generated against the final target — patching after the PUT raced
      // the plan job and flagged the fresh plan as possibly stale).
      if (calorieTarget && calorieTargetDirty) {
        await api.patchProfile({ calorieTarget })
        setCalorieTargetDirty(false)
      }

      const answer = draftAnswers[12] ?? deferredDefaultAnswer(12, locale)
      const putResult = await api.putOnboardingStep(12, {
        step: 12,
        answer,
      } as unknown as OnboardingStepAnswer)
      setCompletedSteps(prev => new Set(prev).add(12))
      // G04: the last onboarding step is saved server-side at this point —
      // the answers themselves are complete regardless of what happens to
      // plan generation below.
      trackFunnelEvent("onboarding_completed")

      let planJobId = putResult.triggeredPlanJobId ?? null
      if (!planJobId) {
        // Fallback per instructions.md T11 notes: "stub the first-plan
        // trigger if T07 lands later" — the plan-generation service may
        // not exist yet, so this call is best-effort.
        try {
          const job = await api.createPlanJob({ trigger: "onboarding" })
          planJobId = job.id
        } catch {
          planJobId = null
        }
      }

      if (!planJobId) {
        // createPlanJob failed outright — step 12 is already saved
        // server-side, but no plan job exists to wait for. Never silently
        // fall through to Access as if a plan were on its way; show a
        // clear, retryable error instead.
        setFinishPhase("error")
        return
      }

      setPendingPlanJobId(planJobId)
      setFinishPhase("waitingForPlan")
      const status = await pollPlanJobStatus(api, planJobId)
      if (status === "timedOut") {
        setFinishPhase("timedOut")
        return
      }

      if (status === "failed") {
        // Review I2: never present a failed job as success — the user sees
        // an honest failure state and continues deliberately.
        setFinishPhase("failed")
        return
      }
      setFinishPhase("ready")
      // Fires once the plan is fully generated, regardless of what happens
      // to free-access activation next.
      trackFunnelEvent("meal_plan_generated")
      // ADR-083 (2026-09-25): the official model is a 14-day, no-card free
      // period, activated automatically — not a paywall the user has to
      // clear. Land on /app/access with autoActivateFreeClaim so it can
      // silently run the same (card-free) Whop checkout a manual "Claim
      // your 2 weeks free" click already uses (AccessPage.tsx), show the
      // "your free access is ready" confirmation, then continue to
      // /app/plan-ready — /app/plan-ready is where a FAILED auto-activation
      // still forwards pendingPlanJobId to /app/access itself, unchanged.
      navigate("/app/access", {
        replace: true,
        state: { pendingPlanJobId: planJobId, autoActivateFreeClaim: true },
      })
      return
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("ONBOARDING_FINISH_ATTEMPT_FAILED", attempt, err)
      if (attempt === 0) {
        await new Promise(resolve => setTimeout(resolve, 1200))
        continue
      }
      setSaveError(err instanceof ApiError ? err.envelope.messageKey : "errors.unknown")
      setFinishErrorDetail(
        err instanceof ApiError
          ? `${err.envelope.code}${err.envelope.correlationId ? ` · ${err.envelope.correlationId}` : ""}`
          : err instanceof Error
            ? err.message
            : String(err)
      )
      setFinishPhase("error")
    }
    }
  }, [api, calorieTarget, calorieTargetDirty, completedSteps, draftAnswers, locale, navigate])

  /** Timed-out or errored: hand off to Access anyway rather than blocking
   * the user forever — Today/Plan already know how to show a still-pending
   * or missing plan honestly. */
  const continueAnyway = useCallback(() => {
    navigate("/app/access", { replace: true, state: { pendingPlanJobId } })
  }, [navigate, pendingPlanJobId])

  return {
    loadState,
    loadError,
    currentStep,
    visualPositionNumber: visualIndex + 1,
    totalSteps: TOTAL_STEPS,
    isFirstStep,
    isLastStep,
    currentAnswer,
    setCurrentAnswer,
    currentStepValid,
    completedSteps,
    draftAnswers,
    calorieTarget,
    saving,
    saveError,
    blocker,
    finishPhase,
    finishErrorDetail,
    pantryExtraction,
    needsHealthConsent,
    recordHealthConsent,
    goBack,
    goNext,
    jumpToStep,
    setIneligible,
    proposeCalorieTarget,
    acceptFloorTarget,
    checkPregnancyGoalConflict,
    clearBlocker,
    extractPantryText,
    confirmPantryCandidates,
    clearPantryExtraction,
    finishOnboarding,
    continueAnyway,
  }
}

async function safeGetHealthConsent(api: ReturnType<typeof useApiClient>): Promise<boolean> {
  try {
    const me = await api.getMe()
    return Boolean(me.healthAiConsent)
  } catch {
    return false
  }
}

async function safeGetProfile(
  api: ReturnType<typeof useApiClient>
): Promise<ProfileResponse | null> {
  try {
    // packages/contracts/src/client.ts types getProfile()'s result as
    // `Profile` (packages/contracts/src/schemas/profile.ts), but the
    // endpoint's actual response schema is `ProfileResponseSchema`
    // (Profile + healthConditions) per schemas/endpoints.ts — a client.ts
    // typing gap outside this task's edit scope (packages/contracts).
    // The runtime payload does carry healthConditions (mock validates
    // against ProfileResponseSchema), so this cast reflects reality.
    return (await api.getProfile()) as unknown as ProfileResponse
  } catch {
    // A brand-new user may not have a profile row yet — onboarding still
    // works without it; the calorie-target review just starts empty.
    return null
  }
}
